import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { PlanFinePrint, StartFreeButton } from '@/components/marketing/free-plan-cta'

const POST_URL = 'https://uptrack.app/blog/synthetic-monitoring-services-guide'
const TITLE = 'Synthetic Monitoring Services: How to Choose One'
const DESCRIPTION =
  'Synthetic monitoring runs scripted checks of your site from outside. What it covers, when plain uptime checks are enough, and how to choose a service.'
const PUBLISHED = '2026-10-12'

const FAQ: { q: string; a: string }[] = [
  {
    q: 'What is synthetic monitoring?',
    a: 'Synthetic monitoring runs scripted checks against a site, an API or a user flow on a fixed schedule, from known locations. The script acts like a user: it loads pages, fills forms, clicks and calls APIs, and reports when a step fails or is slow.',
  },
  {
    q: 'Is an uptime check a synthetic check?',
    a: 'Yes, the simplest kind. An uptime check requests one URL and judges the status code and the time. Synthetic browser checks go further and run several steps in a real browser, such as logging in or paying.',
  },
  {
    q: 'Synthetic monitoring or real user monitoring?',
    a: 'They answer different questions. Synthetic checks are repeatable and run when nobody is on the site, so they catch an outage at 3 a.m. Real user monitoring measures what actual visitors experience, so it shows problems that only some segments see. Most teams use both.',
  },
  {
    q: 'How often should a browser check run?',
    a: 'Run the flow that earns money, such as checkout or sign-up, as often as your plan allows, because it is the one you cannot afford to miss. Run secondary flows less often. Frequent runs cost more on most services.',
  },
  {
    q: 'Do browser checks replace tests in CI?',
    a: 'No. Tests in CI check your code before release. Synthetic checks watch the live system after release, including parts you do not deploy: your host, your DNS, your payment provider and third-party scripts.',
  },
]

export const Route = createFileRoute('/_site/blog/synthetic-monitoring-services-guide')({
  component: SyntheticMonitoringPage,
  head: () => ({
    meta: [
      { title: `${TITLE} — Uptrack` },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: `${TITLE} — Uptrack` },
      {
        property: 'og:description',
        content: 'What synthetic monitoring is, which checks it includes, when you need a browser flow, and what to ask a service before you buy.',
      },
      { property: 'og:image', content: 'https://uptrack.app/og/blog-synthetic-monitoring-services-guide.png' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: POST_URL },
      { property: 'article:published_time', content: PUBLISHED },
    ],
    links: [{ rel: 'canonical', href: POST_URL }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: TITLE,
          description: DESCRIPTION,
          datePublished: PUBLISHED,
          dateModified: PUBLISHED,
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: {
            '@type': 'Organization',
            name: 'Uptrack',
            url: 'https://uptrack.app',
            logo: { '@type': 'ImageObject', url: 'https://uptrack.app/uptrack-logo-header.png' },
          },
          mainEntityOfPage: POST_URL,
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }),
      },
    ],
  }),
})

const p = 'mb-4 text-lg leading-[1.7] text-body'
const link = 'text-primary hover:underline'
const list = 'mb-4 list-disc space-y-2 pl-6 text-lg leading-[1.7] text-body'
const h2 = 'font-sans mb-4 text-2xl font-semibold'
const h3 = 'font-sans mb-2 text-lg font-semibold'

const KINDS: { kind: string; what: string; catches: string }[] = [
  { kind: 'HTTP and API check', what: 'One request: status code, response time, sometimes a header.', catches: 'The server is down, slow or returns an error.' },
  { kind: 'Keyword check', what: 'One request, plus a search for text in the body.', catches: 'The page loads, but shows an error message or an empty shell.' },
  { kind: 'Browser flow', what: 'A script drives a real browser through several steps.', catches: 'Login, sign-up or checkout is broken while every page still returns 200.' },
  { kind: 'Ping, port, DNS, SSL', what: 'Checks below the web layer.', catches: 'A host that does not answer, a wrong DNS record, a certificate about to expire.' },
]

const ASK: { topic: string; question: string }[] = [
  { topic: 'Script language', question: 'Do I write standard Playwright, or a recorder format only this vendor runs?' },
  { topic: 'Evidence on failure', question: 'Do I get a screenshot of the failing step, the console log and the network log, or only a red dot?' },
  { topic: 'Locations', question: 'Where does the browser run, and can I pick the place that matches my users?' },
  { topic: 'False alarms', question: 'Does one failed run page me, or must failures repeat or several locations agree?' },
  { topic: 'Maintenance', question: 'How do I update a script when the page changes, and who gets told when it breaks for a reason that is not an outage?' },
  { topic: 'Pricing model', question: 'Do I pay per monitor, per run or per step? What does one check every minute cost?' },
  { topic: 'Intervals', question: 'What is the fastest interval on the plan I can afford, for browser flows and not only for URL checks?' },
]

function SyntheticMonitoringPage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="mb-3 text-sm text-muted-foreground">October 12, 2026</p>
      <h1 className="font-sans mb-6 text-4xl font-semibold">Synthetic monitoring services: what they are and how to choose one</h1>

      <section className="mb-12">
        <p className={p}>
          Your server returns 200 OK. Your home page loads. Meanwhile the payment form throws a JavaScript error and nobody can buy.
          A plain uptime check sees a healthy site. Synthetic monitoring is the kind of check that sees the broken form.
        </p>
        <p className={p}>
          This guide explains what synthetic monitoring is, which checks it includes, when you need more than an uptime check, and what
          to ask a service before you pay for one. We build Uptrack, so we say at the end where we fit and where we do not.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>What synthetic monitoring is</h2>
        <p className={p}>
          Synthetic monitoring is a script that uses your site on a schedule, from the outside, and tells you when it fails. General
          definitions agree on the shape: scripted simulations of user activity that check performance and availability. The script can
          open pages, complete a transaction or call an API, and it runs from chosen locations at a fixed interval, for example every
          five minutes.
        </p>
        <p className={p}>
          &quot;Synthetic&quot; means no real user is involved. That is the main difference from real user monitoring (RUM), which
          collects data from actual visitors. Because the script behaves the same way every time, with the same browser and the same
          steps, a change in the result points at your system and not at a different visitor. It also runs when nobody visits, so it
          finds the outage at 3 a.m. and works before launch. RUM is better at problems that hit only some visitors, such as one
          country or one device. Most teams use both.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>The four kinds of check</h2>
        <p className={p}>
          &quot;Synthetic monitoring services&quot; covers a range. Start with the cheapest check that catches your failure, and add the
          expensive kind only for flows that earn money.
        </p>
        <div className="mb-4 overflow-x-auto" tabIndex={0} role="region" aria-label="Kinds of synthetic check">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Check</th>
                <th className="pb-3 px-3 text-left font-medium text-muted-foreground">What it does</th>
                <th className="pb-3 pl-3 text-left font-medium text-muted-foreground">What it catches</th>
              </tr>
            </thead>
            <tbody>
              {KINDS.map((row) => (
                <tr key={row.kind} className="border-b last:border-0">
                  <td className="py-3 pr-4 font-medium">{row.kind}</td>
                  <td className="py-3 px-3 text-body">{row.what}</td>
                  <td className="py-3 pl-3 text-body">{row.catches}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={p}>
          An uptime check is a synthetic check with one step. A browser flow has many. The difference in cost is large: a browser needs
          memory and time to start, renders the page, and runs your script, so services limit how many flows you get and how often they
          run.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>When an uptime check is enough, and when it is not</h2>
        <h3 className={h3}>An uptime or keyword check is enough when</h3>
        <ul className={list}>
          <li>The page is mostly content and a failure means the server or the CDN is down.</li>
          <li>An error page is easy to spot with a keyword, such as &quot;Something went wrong&quot;.</li>
          <li>The flow has no state: no login, no cart, no form that talks to a third party.</li>
        </ul>
        <h3 className={h3}>You need a browser flow when</h3>
        <ul className={list}>
          <li>Money moves through the flow: checkout, subscription, top-up.</li>
          <li>A login or sign-up depends on a script that can fail without changing the status code, such as an identity provider or a captcha widget.</li>
          <li>Your single-page app renders an empty shell with a 200 when the API behind it fails.</li>
          <li>A third-party script, such as a payment form, can break the page for every user at once.</li>
        </ul>
        <p className={p}>
          A short test: write down the three things a customer must be able to do this week. If a status code cannot prove that each one
          works, that is your first browser flow.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>What to ask a synthetic monitoring service before you buy</h2>
        <p className={p}>Seven questions separate services faster than a feature table does.</p>
        <ul className="mb-4 space-y-4 text-lg leading-[1.7] text-body">
          {ASK.map((a) => (
            <li key={a.topic}>
              <strong>{a.topic}.</strong> {a.question}
            </li>
          ))}
        </ul>
        <p className={p}>
          Evidence on failure and script language matter most. A failed check without a screenshot sends you to reproduce the problem by
          hand. A vendor-only recorder format means your scripts are lost if you leave. Standard Playwright scripts are portable.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>Pitfalls that make browser checks noisy</h2>
        <h3 className={h3}>Flaky scripts</h3>
        <p className={p}>
          Most flakiness comes from the script, not the site. The{' '}
          <a className={link} href="https://playwright.dev/docs/best-practices" rel="noopener noreferrer">
            Playwright best practices
          </a>{' '}
          recommend locators that match how a user sees the page, such as role and label, web-first assertions that retry until the page is
          ready, and one fresh browser context per run. Retries help, but the{' '}
          <a className={link} href="https://playwright.dev/docs/test-retries" rel="noopener noreferrer">
            retries guide
          </a>{' '}
          treats a pass-on-retry as flaky, not as fixed. A monitor that needs three tries to pass hides a real intermittent bug.
        </p>
        <h3 className={h3}>Test accounts and real money</h3>
        <p className={p}>
          Give the script its own account and a payment method in the provider&apos;s test mode. Never run a monitor that places real orders
          unless you also cancel and refund them. Reset the state at the end of each run, or the account fills with test data.
        </p>
        <h3 className={h3}>Third-party scripts</h3>
        <p className={p}>
          Chat widgets and trackers slow the page and fail on their own. Decide whether a failure in one of them should alert you. Often
          it should not: assert on the elements that matter to the customer, not on every network request.
        </p>
        <h3 className={h3}>Bot protection</h3>
        <p className={p}>
          A firewall or captcha can block a monitor that runs in a data center, and the check then reports an outage that is really a
          block. Allow-list the monitor&apos;s addresses, or send a secret header that your firewall accepts. This is practical advice from
          running checks, not a measured rate. Test it before you rely on the alert.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>Where Uptrack fits</h2>
        <p className={p}>
          Uptrack is an uptime monitor first: HTTP and keyword checks every 30 seconds, plus ping, port, DNS, SSL and heartbeat checks. On
          top of that it runs <strong>real browser checks</strong> that log in, sign up or check out and save a screenshot at the moment a
          step breaks. A run also keeps the console, network and page data of the failure. The scripts are Playwright, so you bring the
          scripts you already write.
        </p>
        <p className={p}>
          Browser monitors are a paid feature. Pro includes 5 browser monitors that run every 10 minutes, Team has 20 every 5 minutes, and
          Business has unlimited monitors every minute. You can see what a browser check shows without an account: the{' '}
          <Link to="/tools/browser-check" className={link}>
            free browser check
          </Link>{' '}
          loads one page in a real browser and returns a screenshot and Core Web Vitals. The{' '}
          <Link to="/features/browser-monitoring" className={link}>
            browser monitoring page
          </Link>{' '}
          lists what each run gives you.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>When Uptrack is not for you</h2>
        <ul className={list}>
          <li>
            Nobody on your team can write and maintain a Playwright script. Our browser checks are scripts, so someone has to own them. A
            no-code recorder fits that team better.
          </li>
          <li>You need load testing, application performance monitoring or real user monitoring. Those are different tools.</li>
          <li>You want browser flows on a free plan. The free plan has HTTP and keyword checks only.</li>
          <li>Your flow sits behind a captcha you cannot bypass for the monitor. No synthetic service will pass it for you.</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className={h2}>FAQ</h2>
        <div className="space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="font-sans mb-1 text-lg font-semibold">{f.q}</h3>
              <p className="text-lg leading-[1.7] text-body">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="rounded-lg border border-border bg-muted p-8 text-center">
        <h2 className="font-sans mb-3 text-xl font-semibold">Start with the check that catches your failure</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">Uptime checks every 30 seconds first. Browser flows when a status code is not proof enough.</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <StartFreeButton />
          <Button size="lg" variant="outline" className="h-11 px-5 text-[15px]" asChild>
            <Link to="/pricing">See plans</Link>
          </Button>
        </div>
        <PlanFinePrint className="mt-4" />
      </div>
    </article>
  )
}
